import { Button } from '@frm/ui';
import type { AppEntry } from '@/types/app';

interface Props {
  /** Имя пользователя для приветствия. Если пусто — generic заголовок. */
  userName?: string;
  featured: AppEntry | null;
  hasRecent: boolean;
  onOpenFeatured: (app: AppEntry) => void;
}

/**
 * V3 split layout:
 *   ┌──────────────────────────────────────┬────────────────────────┐
 *   │  H1 + subtitle + CTAs                │  Featured "Продолжить" │
 *   └──────────────────────────────────────┴────────────────────────┘
 *
 * F4 Smart Hero: если есть `recent` → eyebrow «Продолжить», иначе
 * eyebrow «Рекомендуем начать» — на тот же featured (первый live).
 *
 * SSO-рефактор: Hero больше не рендерит inline-форму входа. Единственная
 * точка авторизации — `/login`. Неавторизованных пользователей LauncherPage
 * редиректит туда до рендера Hero.
 */
export default function Hero({
  userName,
  featured,
  hasRecent,
  onOpenFeatured,
}: Props) {
  const greeting = userName ? `${greetByTime()}, ${firstName(userName)}` : 'Каталог сервисов ФРМ.AI';
  const eyebrow = hasRecent ? 'Продолжить' : 'Рекомендуем начать';

  return (
    <section className="hero">
      <div className="hero__copy">
        <div className="hero__eyebrow">Рабочая панель</div>
        <h1 className="hero__title">{greeting}</h1>
        <p className="hero__subtitle">
          Единая витрина AI-инструментов ФРМ: метрики, рынок, продажи и
          аналитика ренты в одном защищённом входе.
        </p>
        <div className="hero__cta-row">
          <a className="btn btn--primary" href="#all-services">
            Все сервисы
          </a>
          <a
            className="btn btn--ghost"
            href="https://confluence.frm.ru/display/AI"
            target="_blank"
            rel="noreferrer noopener"
          >
            Что нового
          </a>
        </div>
      </div>

      {featured ? (
        <div className="hero__featured">
          <div className="hero__featured-eyebrow">{eyebrow}</div>
          <h2 className="hero__featured-name">
            <span className="hero__featured-icon" aria-hidden="true">{featured.icon}</span>
            {featured.name}
          </h2>
          <p className="hero__featured-desc">{featured.description}</p>
          <Button
            variant="primary"
            onClick={() => onOpenFeatured(featured)}
          >
            Открыть
          </Button>
        </div>
      ) : (
        <div className="hero__featured">
          <div className="hero__featured-eyebrow">Скоро</div>
          <h2 className="hero__featured-name">Готовим релизы</h2>
          <p className="hero__featured-desc">
            Первые сервисы каталога появятся здесь. Следите за обновлениями.
          </p>
        </div>
      )}
    </section>
  );
}

function greetByTime(): string {
  const h = new Date().getHours();
  if (h < 5) return 'Доброй ночи';
  if (h < 12) return 'Доброе утро';
  if (h < 18) return 'Добрый день';
  return 'Добрый вечер';
}

function firstName(displayName: string): string {
  // «Алексей Смирнов» → «Алексей». Если formatted как Email — берём целиком.
  const first = displayName.trim().split(/\s+/)[0];
  return first || displayName;
}
